import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageSquare, Send, User, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function PatientMessages() {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (selectedDoctor) {
      fetchMessages();
      // Polling for new messages every 10 seconds
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedDoctor]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const patientId = user?.patient_id;

      if (!patientId) {
        setLoading(false);
        return;
      }

      // Fetch consultations to get doctor user_ids
      const consultationsRes = await api.get(`/consultations/patient/${patientId}`);
      const consultations = consultationsRes.data || [];

      // Get unique doctor IDs (these are users.id values)
      const doctorIds = [...new Set(consultations.map(c => c.doctor_id))];

      // Fetch doctor details for each doctor
      const doctorsList = await Promise.all(
        doctorIds.map(async (doctorUserId) => {
          try {
            const res = await api.get(`/doctors/${doctorUserId}`);
            return {
              id: doctorUserId,
              name: res.data.full_name || 'Mi Médico',
              specialization: res.data.specialization || 'Médico Tratante'
            };
          } catch {
            // Fallback if doctor details can't be fetched
            return {
              id: doctorUserId,
              name: 'Mi Médico',
              specialization: 'Médico Tratante'
            };
          }
        })
      );

      setDoctors(doctorsList);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedDoctor || !user?.id) return;

    try {
      const response = await api.get(`/messages/${user.id}/${selectedDoctor.id}`);
      setMessages(response.data || []);
    } catch (error) {
      // Silently fail on message fetch - might not have messages yet
      console.error('Error fetching messages:', error);
    }
  };

  const selectDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setMessages([]);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedDoctor || !user?.id) return;

    try {
      setSendingMessage(true);
      await api.post('/messages', {
        sender_id: user.id,
        receiver_id: selectedDoctor.id,
        message: newMessage.trim()
      });

      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar el mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="messages-title">
          Mensajes
        </h1>
        <p className="text-muted-foreground mt-2">
          Comunícate con tus médicos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 250px)' }}>
        {/* Lista de médicos */}
        <Card className="p-4 overflow-y-auto" data-testid="doctors-list">
          <div className="mb-4">
            <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mis Médicos
            </h2>
          </div>
          <div className="space-y-2">
            {doctors.length === 0 ? (
              <div className="text-center py-8">
                <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  No tienes médicos asignados
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Los médicos aparecerán aquí después de tu primera consulta
                </p>
              </div>
            ) : (
              doctors.map((doctor) => (
                <div
                  key={doctor.id}
                  className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                    selectedDoctor?.id === doctor.id
                      ? 'bg-primary/10 border-primary'
                      : 'hover:bg-muted border-transparent'
                  }`}
                  onClick={() => selectDoctor(doctor)}
                  data-testid={`doctor-chat-${doctor.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{doctor.name}</p>
                      <p className="text-xs text-muted-foreground">{doctor.specialization}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Área de chat */}
        <Card className="col-span-2 flex flex-col" data-testid="chat-area">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedDoctor
                ? selectedDoctor.name
                : 'Selecciona un médico'}
            </h2>
            {selectedDoctor && (
              <p className="text-xs text-muted-foreground">{selectedDoctor.specialization}</p>
            )}
          </div>

          <div className="flex-1 p-4 overflow-y-auto" data-testid="messages-container">
            {!selectedDoctor ? (
              <div className="flex flex-col items-center justify-center h-full">
                <MessageSquare className="w-16 h-16 text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-center">
                  Selecciona un médico para iniciar una conversación
                </p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-muted-foreground text-center">
                  No hay mensajes con este médico
                </p>
                <p className="text-sm text-muted-foreground text-center mt-2">
                  Inicia una conversación enviando un mensaje
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${
                      msg.sender_id === user.id ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-lg ${
                        msg.sender_id === user.id
                          ? 'bg-primary text-white'
                          : 'bg-muted'
                      }`}
                    >
                      <p className="text-sm">{msg.message}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {new Date(msg.created_at).toLocaleTimeString('es-PE', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                placeholder="Escribe un mensaje..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={!selectedDoctor || sendingMessage}
                data-testid="message-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!selectedDoctor || !newMessage.trim() || sendingMessage}
                data-testid="send-message-button"
              >
                {sendingMessage ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            {selectedDoctor && (
              <p className="text-xs text-muted-foreground mt-2">
                Presiona Enter para enviar el mensaje
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
